/**
 * Takeoff dialogs — NewProject, EditProject, FindJobs, Confirm,
 * LibraryItem, PipeBuilder (job-only pipe), DuctBuilder (job-only duct),
 * CustomItem. White cards / slate borders / orange accents per the module
 * spec; Escape and backdrop clicks close.
 */
import { useEffect, useMemo, useRef, useCallback, useState } from "react";
import { X } from "lucide-react";
import { PIPE_SYSTEMS, SIZES, THICKNESSES, PVC_GRID, ALUMINUM_X_PVC, SUPPORT_GRID, SUPPORT_SPACING_FT, ductPerimeterFt, DUCT_INSULATION_RATE, DUCT_VENTURE_CLAD_RATE, money } from "./pricing";
import { BTN_GHOST, BTN_ORANGE, INPUT, LABEL, MONO } from "./theme";

export const Overlay = ({ open, onClose, children, wide, xl }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4" data-testid="takeoff-dialog-overlay">
      <div className="absolute inset-0 bg-[#07101f]/60 backdrop-blur-sm" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        className={`relative max-h-[88vh] w-full ${xl ? "max-w-4xl" : wide ? "max-w-2xl" : "max-w-md"} overflow-y-auto rounded-2xl border border-slate-200 bg-white p-5 text-slate-800 shadow-2xl`}
      >
        {children}
      </div>
    </div>
  );
};

export const DialogTitle = ({ children, onClose }) => (
  <div className="mb-3 flex items-center justify-between">
    <h3 className="text-lg font-bold text-slate-900">{children}</h3>
    <button onClick={onClose} aria-label="Close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"><X className="h-4 w-4" /></button>
  </div>
);

/**
 * NotesDialog — click a Notes cell on Home to read/edit the whole note.
 * Autosaves after a short typing pause; closing (Done, X, Esc, backdrop)
 * flushes pending edits first and STAYS OPEN with a Retry if the save
 * failed — a draft is never silently discarded.
 */
export function NotesDialog({ open, onClose, project, onSave }) {
  const [draft, setDraft] = useState("");
  const [state, setState] = useState("saved"); // saved | dirty | saving | error
  const savedRef = useRef("");
  const draftRef = useRef("");
  const timer = useRef(null);
  draftRef.current = draft;

  useEffect(() => {
    if (open) {
      const n = project?.notes || "";
      setDraft(n);
      draftRef.current = n;
      savedRef.current = n;
      setState("saved");
    }
    return () => clearTimeout(timer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, project?.project_id]);

  const flush = useCallback(async () => {
    clearTimeout(timer.current);
    if (draftRef.current === savedRef.current) { setState("saved"); return true; }
    setState("saving");
    try {
      await onSave(draftRef.current);
      savedRef.current = draftRef.current;
      setState("saved");
      return true;
    } catch {
      setState("error");
      return false;
    }
  }, [onSave]);

  const change = (v) => {
    setDraft(v);
    setState("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 800);
  };

  const close = async () => {
    if (await flush()) onClose();
  };

  return (
    <Overlay open={open} onClose={close}>
      <DialogTitle onClose={close}>Notes — {project?.name}</DialogTitle>
      <textarea
        autoFocus
        value={draft}
        onChange={(e) => change(e.target.value)}
        rows={10}
        placeholder="Type the job's notes…"
        className={`${INPUT} min-h-[200px] w-full resize-y whitespace-pre-wrap leading-relaxed`}
        data-testid="notes-editor-textarea"
      />
      <div className="mt-3 flex items-center justify-between gap-2">
        <p className={`text-xs ${state === "error" ? "font-semibold text-red-600" : "text-slate-400"}`} data-testid="notes-save-status" aria-live="polite">
          {state === "saving" ? "Saving…" : state === "saved" ? "Saved" : state === "dirty" ? "Editing…" : "Couldn't save — your text is still here."}
        </p>
        <div className="flex gap-2">
          {state === "error" && <button className={BTN_GHOST} onClick={flush} data-testid="notes-retry">Retry</button>}
          <button className={BTN_ORANGE} onClick={close} data-testid="notes-done">Done</button>
        </div>
      </div>
    </Overlay>
  );
}

export function NewProjectDialog({ open, onClose, onCreate }) {
  const [name, setName] = useState("");
  const [due, setDue] = useState("");
  useEffect(() => { if (open) { setName(""); setDue(""); } }, [open]);
  const submit = () => {
    if (!name.trim()) return;
    // Spec: no date picked -> save today's local date automatically.
    const today = new Date();
    const local = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
    onCreate({ name: name.trim(), dueDate: due || local });
    onClose();
  };
  return (
    <Overlay open={open} onClose={onClose}>
      <DialogTitle onClose={onClose}>New job</DialogTitle>
      <div className="space-y-3">
        <div><label className={LABEL}>Project name</label>
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} data-testid="takeoff-new-project-name" className={INPUT} placeholder="26-0922 Example Fit-up-123 Main St" /></div>
        <div><label className={LABEL}>Due date</label>
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="takeoff-new-project-due" className={INPUT} /></div>
        <p className="text-[11px] text-slate-400">No date picked? Today's date is saved automatically.</p>
        <div className="flex justify-end gap-2 pt-1">
          <button className={BTN_GHOST} onClick={onClose}>Cancel</button>
          <button className={BTN_ORANGE} onClick={submit} disabled={!name.trim()} data-testid="takeoff-new-project-create">Create job</button>
        </div>
      </div>
    </Overlay>
  );
}

export function EditProjectDialog({ open, onClose, project, onSave }) {
  const [name, setName] = useState("");
  const [due, setDue] = useState("");
  const [notes, setNotes] = useState("");
  useEffect(() => {
    if (open && project) { setName(project.name || ""); setDue(project.due_date || ""); setNotes(project.notes || ""); }
  }, [open, project]);
  return (
    <Overlay open={open} onClose={onClose}>
      <DialogTitle onClose={onClose}>Edit job</DialogTitle>
      <div className="space-y-3">
        <div><label className={LABEL}>Project name</label>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="takeoff-edit-project-name" className={INPUT} /></div>
        <div><label className={LABEL}>Due date</label>
          <input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="takeoff-edit-project-due" className={INPUT} /></div>
        <div><label className={LABEL}>Project notes</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} data-testid="takeoff-edit-project-notes" className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-[#f97316] focus:outline-none focus:ring-1 focus:ring-[#f97316]" /></div>
        <div className="flex justify-end gap-2 pt-1">
          <button className={BTN_GHOST} onClick={onClose}>Cancel</button>
          <button className={BTN_ORANGE} onClick={() => { onSave({ name: name.trim(), dueDate: due || null, notes }); onClose(); }} disabled={!name.trim()} data-testid="takeoff-edit-project-save">Save</button>
        </div>
      </div>
    </Overlay>
  );
}

export function FindJobsDialog({ open, onClose, projects, statuses, onOpen, onStatus }) {
  const [q, setQ] = useState("");
  useEffect(() => { if (open) setQ(""); }, [open]);
  const list = useMemo(() => projects
    .filter((p) => p.name.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => (b.created_at || 0) - (a.created_at || 0)), [projects, q]);
  return (
    <Overlay open={open} onClose={onClose} wide>
      <DialogTitle onClose={onClose}>Find jobs</DialogTitle>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search project names" data-testid="takeoff-find-jobs-search" className={`${INPUT} mb-3`} />
      <div className="max-h-[50vh] space-y-1 overflow-y-auto">
        {list.map((p) => (
          <div key={p.project_id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50">
            <button className="min-w-0 flex-1 truncate text-left text-sm font-medium text-slate-800 hover:text-[#f97316]" onClick={() => { onOpen(p.project_id); onClose(); }} data-testid={`takeoff-find-open-${p.project_id}`}>
              {p.name}
            </button>
            <select value={p.status} onChange={(e) => onStatus(p.project_id, e.target.value)} data-testid={`takeoff-find-status-${p.project_id}`}
              aria-label={`Status for ${p.name}`}
              className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs text-slate-700 focus:border-[#f97316] focus:outline-none">
              {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        ))}
        {!list.length && <p className="py-6 text-center text-sm text-slate-400">No jobs match.</p>}
      </div>
    </Overlay>
  );
}

export function ConfirmDialog({ open, onClose, title, body, confirmLabel = "Delete", onConfirm }) {
  return (
    <Overlay open={open} onClose={onClose}>
      <DialogTitle onClose={onClose}>{title}</DialogTitle>
      <p className="mb-4 text-sm text-slate-600">{body}</p>
      <div className="flex justify-end gap-2">
        <button className={BTN_GHOST} onClick={onClose}>Cancel</button>
        <button className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-red-500" onClick={() => { onConfirm(); onClose(); }} data-testid="takeoff-confirm-delete">{confirmLabel}</button>
      </div>
    </Overlay>
  );
}

const toolFor = { linear: "Linear feet", area: "Square feet", count: "Item count" };
const unitFor = { linear: "ft", area: "sq ft", count: "ea" };

export function LibraryItemDialog({ open, onClose, onSave, item }) {
  const [f, setF] = useState({ name: "", category: "Pipe", tool: "linear", rate: 0, color: "#2563eb" });
  useEffect(() => {
    if (open) setF(item ? { name: item.name, category: item.category, tool: item.tool, rate: item.rate, color: item.color } : { name: "", category: "Pipe", tool: "linear", rate: 0, color: "#2563eb" });
  }, [open, item]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <Overlay open={open} onClose={onClose}>
      <DialogTitle onClose={onClose}>{item ? "Edit library item" : "New library item"}</DialogTitle>
      <div className="space-y-3">
        <div><label className={LABEL}>Name</label><input value={f.name} onChange={(e) => set("name", e.target.value)} className={INPUT} data-testid="takeoff-lib-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={LABEL}>Category</label>
            <select value={f.category} onChange={(e) => set("category", e.target.value)} className={INPUT} data-testid="takeoff-lib-category">
              {["Pipe", "Duct", "Count"].map((c) => <option key={c}>{c}</option>)}
            </select></div>
          <div><label className={LABEL}>Measurement</label>
            <select value={f.tool} onChange={(e) => set("tool", e.target.value)} className={INPUT} data-testid="takeoff-lib-tool">
              {Object.entries(toolFor).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><label className={LABEL}>Unit price ({unitFor[f.tool]})</label>
            <input type="number" min="0" step="0.01" value={f.rate} onChange={(e) => set("rate", Number(e.target.value) || 0)} className={INPUT} data-testid="takeoff-lib-rate" /></div>
          <div><label className={LABEL}>Colour</label>
            <input type="color" value={f.color} onChange={(e) => set("color", e.target.value)} aria-label="Colour" className="h-9 w-full cursor-pointer rounded-lg border border-slate-300 bg-white" data-testid="takeoff-lib-color" /></div>
        </div>
        <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Line and area items always draw with the 4 px standard.</p>
        <div className="flex justify-end gap-2 pt-1">
          <button className={BTN_GHOST} onClick={onClose}>Cancel</button>
          <button className={BTN_ORANGE} disabled={!f.name.trim()} onClick={() => { onSave({ ...f, unit: unitFor[f.tool], lineWidth: 4 }); onClose(); }} data-testid="takeoff-lib-save">{item ? "Save" : "Add to library"}</button>
        </div>
      </div>
    </Overlay>
  );
}

/** Structured pipe builder — shared by the job palette (mode="job", price
 * looked up from the company library) and the Company Library itself
 * (mode="library", price is defined here and editable). Same fields, same
 * naming, same PVC/support math in both modes. */
export function PipeBuilderDialog({ open, onClose, library, onSave, editItem, mode = "job" }) {
  const isLibrary = mode === "library";
  const [size, setSize] = useState('1"');
  const [thick, setThick] = useState('1"');
  const [service, setService] = useState(PIPE_SYSTEMS[0].name);
  const [custom, setCustom] = useState("");
  const [color, setColor] = useState(PIPE_SYSTEMS[0].color);
  const [rate, setRate] = useState(0);
  useEffect(() => {
    if (!open) return;
    if (editItem) {
      setCustom(""); setColor(editItem.color || PIPE_SYSTEMS[0].color);
      setRate(Number(editItem.rate) || 0);
      const m = /^([\d./-]+)"\s+(.+?)\s+([\d.]+)"\s+INS/i.exec(editItem.name || "");
      if (m) {
        setSize(m[1] + '"'); setThick(m[3] + '"');
        const svc = PIPE_SYSTEMS.find((s) => m[2].startsWith(s.name));
        if (svc) {
          setService(svc.name);
          // preserve the " - Custom" suffix so re-saving keeps the full name
          const rest = m[2].slice(svc.name.length).replace(/^\s*-\s*/, "").trim();
          if (rest) setCustom(rest);
        } else {
          setService("Other"); setCustom(m[2]);
        }
      }
    } else {
      setSize('1"'); setThick('1"'); setService(PIPE_SYSTEMS[0].name); setCustom(""); setColor(PIPE_SYSTEMS[0].color);
      setRate(0);
    }
  }, [open, editItem]); // eslint-disable-line
  const name = service === "Other"
    ? `${size} ${custom.trim()} ${thick} INS`
    : custom.trim()
      ? `${size} ${service} - ${custom.trim()} ${thick} INS`
      : `${size} ${service} ${thick} INS`;
  const libMatch = useMemo(() => (library || []).find((l) => l.name === name && l.item_id !== editItem?.item_id), [library, name, editItem]);
  const baseRate = isLibrary ? (Number(rate) || 0) : (libMatch ? libMatch.rate : 0);
  const pvcRate = PVC_GRID[size]?.[thick] ?? 0;
  const supportRate = SUPPORT_GRID[size === '1.5"' ? '1-1/2"' : size]?.[thick] ?? 0;
  const valid = isLibrary
    ? (service !== "Other" || !!custom.trim())
    : baseRate > 0 && (service !== "Other" || !!custom.trim());
  return (
    <Overlay open={open} onClose={onClose}>
      <DialogTitle onClose={onClose}>{editItem ? (isLibrary ? "Edit library pipe" : "Edit job pipe") : (isLibrary ? "New library pipe" : "New pipe for this job")}</DialogTitle>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div><label className={LABEL}>Pipe size</label>
            <select value={size} onChange={(e) => setSize(e.target.value)} className={INPUT} data-testid="pipe-builder-size">
              {SIZES.map((s) => <option key={s}>{s}</option>)}
            </select></div>
          <div><label className={LABEL}>Insulation thickness</label>
            <select value={thick} onChange={(e) => setThick(e.target.value)} className={INPUT} data-testid="pipe-builder-thickness">
              {THICKNESSES.map((t) => <option key={t}>{t}</option>)}
            </select></div>
        </div>
        <div><label className={LABEL}>Pipe service</label>
          <select value={service} onChange={(e) => { setService(e.target.value); const s2 = PIPE_SYSTEMS.find((x) => x.name === e.target.value); if (s2) setColor(s2.color); }} className={INPUT} data-testid="pipe-builder-service">
            {PIPE_SYSTEMS.map((s) => <option key={s.name}>{s.name}</option>)}
          </select></div>
        <div><label className={LABEL}>Custom name {service === "Other" && <span className="text-red-500">(required)</span>}</label>
          <input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder={service === "Other" ? "e.g. Glycol" : "optional, e.g. Building 2 loop"} className={INPUT} data-testid="pipe-builder-custom" /></div>
        {isLibrary && (
          <div><label className={LABEL}>Base rate ($ / ft)</label>
            <input type="number" min="0" step="0.01" value={rate} onChange={(e) => setRate(e.target.value)} className={INPUT} data-testid="pipe-builder-rate" />
            {!editItem && libMatch && (
              <button type="button" onClick={() => setRate(libMatch.rate)} className="mt-1 text-[11px] font-semibold text-[#f97316] hover:underline" data-testid="pipe-builder-use-builtin">
                Use the built-in price for this combination ({money(libMatch.rate)}/ft)
              </button>
            )}
          </div>
        )}
        <div><label className={LABEL}>Colour</label>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} aria-label="Colour" className="h-9 w-full cursor-pointer rounded-lg border border-slate-300 bg-white" data-testid="pipe-builder-color" /></div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Preview</p>
          <p className="mt-1 text-sm font-semibold text-slate-900" data-testid="pipe-builder-preview">{name}</p>
          <div className="mt-2 grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
            <div className="rounded-lg border border-slate-200 bg-white p-2"><p className="text-[9px] font-semibold uppercase text-slate-400">Base / ft</p><p className={`${MONO} text-sm font-bold text-slate-900`} data-testid="pipe-builder-base-preview">{money(baseRate)}</p></div>
            <div className="rounded-lg border border-slate-200 bg-white p-2"><p className="text-[9px] font-semibold uppercase text-slate-400">With PVC / ft</p><p className={`${MONO} text-sm font-bold text-slate-900`} data-testid="pipe-builder-pvc-preview">{money(pvcRate)}</p></div>
            <div className="rounded-lg border border-slate-200 bg-white p-2"><p className="text-[9px] font-semibold uppercase text-slate-400">Aluminum / ft</p><p className={`${MONO} text-sm font-bold text-slate-900`} data-testid="pipe-builder-alum-preview">{money(Math.round(pvcRate * ALUMINUM_X_PVC * 100) / 100)}</p></div>
            <div className="rounded-lg border border-slate-200 bg-white p-2"><p className="text-[9px] font-semibold uppercase text-slate-400">Support ea</p><p className={`${MONO} text-sm font-bold text-slate-900`}>{money(supportRate)}</p></div>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">PVC replaces the base rate; Aluminum is {ALUMINUM_X_PVC}× the PVC rate. Supports are automatic every {SUPPORT_SPACING_FT} ft except on Riser-labeled takeoffs.</p>
        </div>
        {!isLibrary && !valid && baseRate <= 0 && <p className="text-xs text-red-600">This size/thickness isn't priced — pick another combination.</p>}
        {isLibrary && baseRate <= 0 && <p className="text-xs text-amber-600">No price yet — jobs can still measure with it, but it will estimate at $0.</p>}
        <div className="flex justify-end gap-2 pt-1">
          <button className={BTN_GHOST} onClick={onClose}>Cancel</button>
          <button className={BTN_ORANGE} disabled={!valid} onClick={() => { onSave({ name, category: "Pipe", tool: "linear", unit: "ft", rate: baseRate, color, lineWidth: 4, ...(isLibrary ? {} : { libraryId: libMatch?.item_id }) }); onClose(); }} data-testid="pipe-builder-save">
            {editItem ? "Save pipe" : isLibrary ? "Add pipe to library" : "Add pipe to job"}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

/** Service presets (Oct-13 list #1): clicking one sets the service name AND
 * its default colour immediately — used for the duct item and its drawing. */
const DUCT_SERVICES = [
  { name: "Supply", color: "#dc2626" },    // red
  { name: "Exhaust", color: "#2563eb" },   // blue
  { name: "Fresh Air", color: "#dc2626" }, // red
  { name: "Intake", color: "#ec4899" },    // pink
];
const DUCT_UNITS_KEY = "takeoff-duct-units";
const getDuctUnits = () => {
  try { return localStorage.getItem(DUCT_UNITS_KEY) === "mm" ? "mm" : "in"; } catch { return "in"; }
};

/** Job-only duct builder — exterior surface from traced linear feet. */
export function DuctBuilderDialog({ open, onClose, onSave, editItem }) {
  const [labelTxt, setLabelTxt] = useState("");
  const [shape, setShape] = useState("rect");
  const [unitSys, setUnitSys] = useState(getDuctUnits);
  const [w, setW] = useState(12);
  const [h, setH] = useState(12);
  const [d, setD] = useState(12);
  const [color, setColor] = useState("#3b82f6");
  useEffect(() => {
    if (!open) return;
    if (editItem?.ductSpec) {
      const s = editItem.ductSpec;
      setShape(s.shape || (s.d != null ? "round" : "rect"));
      setUnitSys(s.unit || "in");
      setW(s.w ?? 12); setH(s.h ?? 12); setD(s.d ?? 12);
      setColor(editItem.color || "#3b82f6"); setLabelTxt(s.label || "");
    } else {
      // new ducts open on the estimator's remembered units — never reset
      setLabelTxt(""); setShape("rect"); setUnitSys(getDuctUnits()); setW(12); setH(12); setD(12); setColor("#3b82f6");
    }
  }, [open, editItem]);
  /** Explicit unit switch: convert entered dimensions so the physical size
   * stays the same, and remember the choice across sessions. */
  const switchUnits = (next) => {
    if (next === unitSys) return;
    const conv = (v) => next === "mm"
      ? Math.round((Number(v) || 0) * 25.4)
      : Math.round(((Number(v) || 0) / 25.4) * 100) / 100;
    setW(conv(w)); setH(conv(h)); setD(conv(d));
    setUnitSys(next);
    try { localStorage.setItem(DUCT_UNITS_KEY, next); } catch { /* private mode */ }
  };
  const spec = shape === "round"
    ? { shape: "round", d: Number(d) || 0, unit: unitSys, label: labelTxt.trim() }
    : { shape: "rect", w: Number(w) || 0, h: Number(h) || 0, unit: unitSys, label: labelTxt.trim() };
  const dimTxt = shape === "round"
    ? (unitSys === "in" ? `${spec.d}" Round` : `${spec.d} mm Round`)
    : (unitSys === "in" ? `${spec.w}" × ${spec.h}"` : `${spec.w} × ${spec.h} mm`);
  const name = `${dimTxt}${spec.label ? ` ${spec.label}` : ""}`;
  // ductPerimeterFt reads spec.unit and converts exactly once — no
  // pre-conversion here (double conversion was the Oct-14 mm bug's twin)
  const perFt = ductPerimeterFt(spec);
  const valid = name.trim().length > 0 && perFt > 0;
  return (
    <Overlay open={open} onClose={onClose}>
      <DialogTitle onClose={onClose}>{editItem ? "Edit job duct" : "Add duct to this job"}</DialogTitle>
      <div className="space-y-3">
        <div><label className={LABEL}>Duct name / service (optional)</label>
          <input value={labelTxt} onChange={(e) => setLabelTxt(e.target.value)} className={INPUT} placeholder="Supply" data-testid="duct-builder-name" />
          <div className="mt-1 flex gap-1">{DUCT_SERVICES.map((s) => (
            <button key={s.name} type="button" onClick={() => { setLabelTxt(s.name); setColor(s.color); }}
              className="rounded-md border border-slate-300 px-2 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-100"
              data-testid={`duct-builder-service-${s.name.replace(/\s+/g, "-").toLowerCase()}`}>
              <span className="mr-1 inline-block h-2 w-2 rounded-full align-middle" style={{ background: s.color }} />{s.name}
            </button>
          ))}</div></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={LABEL}>Shape</label>
            <select value={shape} onChange={(e) => setShape(e.target.value)} className={INPUT} data-testid="duct-builder-shape">
              <option value="rect">Rectangular</option><option value="round">Round</option>
            </select></div>
          <div><label className={LABEL}>Units</label>
            <select value={unitSys} onChange={(e) => switchUnits(e.target.value)} className={INPUT} data-testid="duct-builder-units">
              <option value="in">Inches</option><option value="mm">Millimetres</option>
            </select></div>
        </div>
        {shape === "rect" ? (
          <div className="grid grid-cols-2 gap-3">
            <div><label className={LABEL}>Width ({unitSys})</label><input type="number" min="1" value={w} onChange={(e) => setW(e.target.value)} className={INPUT} data-testid="duct-builder-w" /></div>
            <div><label className={LABEL}>Height ({unitSys})</label><input type="number" min="1" value={h} onChange={(e) => setH(e.target.value)} className={INPUT} data-testid="duct-builder-h" /></div>
          </div>
        ) : (
          <div><label className={LABEL}>Diameter ({unitSys})</label><input type="number" min="1" value={d} onChange={(e) => setD(e.target.value)} className={INPUT} data-testid="duct-builder-d" /></div>
        )}
        <div><label className={LABEL}>Colour</label>
          <input type="color" value={color} onChange={(e) => setColor(e.target.value)} aria-label="Colour" className="h-9 w-full cursor-pointer rounded-lg border border-slate-300 bg-white" data-testid="duct-builder-color" /></div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Preview</p>
          <p className="mt-1 text-sm font-semibold text-slate-900" data-testid="duct-builder-preview">{name}</p>
          <p className={`mt-1 ${MONO} text-xs text-slate-600`}>{perFt.toFixed(2)} sq ft of surface per linear foot — 10 ft traced = {(perFt * 10).toFixed(1)} sq ft = {money(perFt * 10 * DUCT_INSULATION_RATE)} (or {money(perFt * 10 * DUCT_VENTURE_CLAD_RATE)} Venture Clad)</p>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button className={BTN_GHOST} onClick={onClose}>Cancel</button>
          <button className={BTN_ORANGE} disabled={!valid} onClick={() => { onSave({ name, category: "Duct", tool: "linear", unit: "ft", rate: DUCT_INSULATION_RATE, color, lineWidth: 4, ductSpec: spec }); onClose(); }} data-testid="duct-builder-save">
            {editItem ? "Save duct" : "Add duct to job"}
          </button>
        </div>
      </div>
    </Overlay>
  );
}

/** Quick custom job item (non-pipe/duct): count or area with free pricing,
 * and an explicit price destination — Pipe insulation, Duct insulation,
 * Rigid supports, or a specific extra (existing or newly created). */
export function CustomItemDialog({ open, onClose, onSave, extras = [], editItem = null }) {
  const [f, setF] = useState({ name: "", tool: "count", rate: 0, color: "#f97316" });
  const [dest, setDest] = useState("pipe");        // "pipe" | "duct" | "supports" | "extra"
  const [extraId, setExtraId] = useState("");      // existing extra id, or "__new"
  const [newExtraName, setNewExtraName] = useState("");
  useEffect(() => {
    if (!open) return;
    setF(editItem
      ? { name: editItem.name || "", tool: editItem.tool || "count", rate: editItem.rate || 0, color: editItem.color || "#f97316" }
      : { name: "", tool: "count", rate: 0, color: "#f97316" });
    const pt = editItem?.priceTo || "pipe";
    if (pt.startsWith("extra:")) { setDest("extra"); setExtraId(pt.slice(6)); }
    else { setDest(pt); setExtraId(""); }
    setNewExtraName("");
  }, [open]); // eslint-disable-line
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const destValid = dest !== "extra" || !!extraId;
  const priceTo = dest !== "extra" ? dest
    : extraId === "__new" ? `extra:new:${newExtraName.trim()}`
    : `extra:${extraId}`;
  return (
    <Overlay open={open} onClose={onClose}>
      <DialogTitle onClose={onClose}>{editItem ? "Edit custom item" : "Custom item for this job"}</DialogTitle>
      <div className="space-y-3">
        <div><label className={LABEL}>Name</label><input value={f.name} onChange={(e) => set("name", e.target.value)} className={INPUT} data-testid="custom-item-name" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className={LABEL}>Measurement</label>
            <select value={f.tool} onChange={(e) => set("tool", e.target.value)} className={INPUT} data-testid="custom-item-tool">
              {Object.entries(toolFor).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select></div>
          <div><label className={LABEL}>Unit price ({unitFor[f.tool]})</label>
            <input type="number" min="0" step="0.01" value={f.rate} onChange={(e) => set("rate", Number(e.target.value) || 0)} className={INPUT} data-testid="custom-item-rate" /></div>
        </div>
        <div><label className={LABEL}>Assign price to</label>
          <select value={dest} onChange={(e) => { setDest(e.target.value); if (e.target.value !== "extra") setExtraId(""); }} className={INPUT} data-testid="custom-item-destination">
            <option value="pipe">Pipe insulation</option>
            <option value="duct">Duct insulation</option>
            <option value="supports">Rigid supports</option>
            <option value="extra">Extra…</option>
          </select></div>
        {dest === "extra" && (
          <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-2.5">
            <select value={extraId} onChange={(e) => setExtraId(e.target.value)} className={INPUT} data-testid="custom-item-extra">
              <option value="">Choose an extra…</option>
              {extras.map((x) => <option key={x.id} value={x.id}>{x.name || "Untitled extra"}</option>)}
              <option value="__new">＋ New extra</option>
            </select>
            {extraId === "__new" && (
              <input value={newExtraName} onChange={(e) => setNewExtraName(e.target.value)}
                placeholder="Extra name (optional — e.g. CSI-070)" className={INPUT} data-testid="custom-item-new-extra-name" />
            )}
          </div>
        )}
        <div><label className={LABEL}>Colour</label>
          <input type="color" value={f.color} onChange={(e) => set("color", e.target.value)} aria-label="Colour" className="h-9 w-full cursor-pointer rounded-lg border border-slate-300 bg-white" /></div>
        <div className="flex justify-end gap-2 pt-1">
          <button className={BTN_GHOST} onClick={onClose}>Cancel</button>
          <button className={BTN_ORANGE} disabled={!f.name.trim() || !destValid}
            onClick={() => { onSave({ ...f, category: f.tool === "count" ? "Count" : "Pipe", unit: unitFor[f.tool], lineWidth: 4, priceTo }); onClose(); }}
            data-testid="custom-item-save">{editItem ? "Save item" : "Add to job"}</button>
        </div>
      </div>
    </Overlay>
  );
}
